import classNames from "classnames";
import { FC, useMemo, useState } from "react";
import styles from "./ColorPicker.module.css";
import { isValidHex, normalizeHex } from "../../utils/color";
import { Input } from "@rothoblaas/design-system";
import useI18n from "../../hooks/useTranslations";
import { DEFAULT_UI_CATEGORY_COLOR } from "../../constants/colors";

interface ColorPickerProps {
  value: string | null;
  onChange: (hex: string | null) => void;
  disabled?: boolean;
  id?: string;
  isInvalid?: boolean;
}

const ColorPicker: FC<ColorPickerProps> = ({ value, onChange, disabled = false, id, isInvalid = false }) => {
  const i18n = useI18n();
  const normalizedValue = useMemo(() => {
    if (!value) {
      return null;
    }

    const normalized = normalizeHex(value);
    return isValidHex(normalized) ? normalized : null;
  }, [value]);

  const [hexDraft, setHexDraft] = useState(normalizedValue ?? "");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasInvalidDraft = hexDraft.length > 0 && !isValidHex(hexDraft);

  const colorInputValue = normalizedValue ?? DEFAULT_UI_CATEGORY_COLOR;
  const invalid = isInvalid || hasInvalidDraft;

  const errorId = id ? `${id}-color-error` : undefined;
  const describedBy = errorMessage && errorId ? errorId : undefined;

  const applyDraft = () => {
    const normalizedDraft = normalizeHex(hexDraft);

    if (!normalizedDraft) {
      setErrorMessage(null);
      onChange(null);
      setHexDraft("");
      return;
    }

    if (isValidHex(normalizedDraft)) {
      setErrorMessage(null);
      onChange(normalizedDraft);
      setHexDraft(normalizedDraft);
      return;
    }

    setErrorMessage(i18n.translation.common.colorHexInvalid);
  };

  return (
    <div className={classNames(styles.wrapper, { [styles.invalid]: invalid, [styles.disabled]: disabled })}>
      <div className={styles.controls}>
        <input
          id={id}
          type="color"
          className={styles.colorInput}
          disabled={disabled}
          value={colorInputValue}
          aria-label="Color picker"
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={(event) => {
            const next = normalizeHex(event.target.value);
            setHexDraft(next);
            setErrorMessage(null);
            onChange(next);
          }}
        />

        <Input
          className={styles.hexInput}
          disabled={disabled}
          value={hexDraft}
          placeholder="#RRGGBB"
          aria-label="Color hex value"
          aria-invalid={invalid}
          aria-describedby={describedBy}
          isInvalid={invalid}
          forceUpper
          onChange={(event) => {
            setHexDraft(normalizeHex(event.target.value));
            setErrorMessage(null);
          }}
          onBlur={applyDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              applyDraft();
            }
          }}
        />
      </div>

      {errorMessage && (
        <div id={errorId} className={styles.errorText} role="alert" aria-live="polite">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
